// =============================================================================
// features/dashboard/DashboardPage.tsx — Dashboard real com KPIs e gráficos.
//
// Consome GET /api/dashboard/metrics via useDashboardMetrics (TanStack Query).
//
// Layout em DUAS ZONAS explícitas (diagnóstico D1):
//   "No período"  — tudo que obedece ao seletor: KPIs, série diária, status,
//                   canais, cidades, origem, top agentes.
//   "Agora"       — estado atual: base de leads, funil kanban, tempo por
//                   estágio, leads parados. Selo de snapshot, sem rótulo de
//                   período.
//
// Antes havia um único seletor governando visualmente 13 métricas das quais só 5
// o respeitavam de fato — a origem da desconfiança relatada pela operação.
//
// Filtros: período (com opção personalizada) + cidade. Gráficos: SVG manual.
// DS: light-first, tokens canônicos, profundidade elev-2, hover Spotlight.
// =============================================================================

import * as React from 'react';

import { FreshnessIndicator } from '../../components/ui/FreshnessIndicator';
import type { LeadsByCityItem, Range } from '../../hooks/dashboard/types';
import { useDashboardMetrics } from '../../hooks/dashboard/useDashboardMetrics';
import { useAuth } from '../auth/useAuth';
import { ContextualHelp } from '../help/contextual';

import { AvgDaysInStageChart, AvgDaysInStageChartSkeleton } from './AvgDaysInStageChart';
import { ChannelBars, ChannelBarsSkeleton } from './components/ChannelBars';
import { CityList, CityListSkeleton } from './components/CityList';
import { DailySeriesChart, DailySeriesChartSkeleton } from './components/DailySeriesChart';
import { KanbanBars, KanbanBarsSkeleton } from './components/KanbanBars';
import { OriginChannelList } from './components/OriginChannelList';
import { SnapshotRow, SnapshotRowSkeleton } from './components/SnapshotRow';
import { StaleBanner } from './components/StaleBanner';
import { StatsRow, StatsRowSkeleton } from './components/StatsRow';
import { StatusDonut, StatusDonutSkeleton } from './components/StatusDonut';
import { TopAgentsTable, TopAgentsTableSkeleton } from './components/TopAgentsTable';
import { ZoneHeading } from './components/ZoneHeading';
import { buildCityOptions } from './utils/cityOptions';

// ---------------------------------------------------------------------------
// Range options
// ---------------------------------------------------------------------------

// Valores canônicos (o backend ainda aceita os aliases legados 7d/30d/mtd).
// "Ontem" e "Esta semana" entraram porque são os dois cortes que a operação usa
// no fechamento diário e não existiam em nenhuma das duas telas.
const RANGE_OPTIONS: { value: Range; label: string }[] = [
  { value: 'today', label: 'Hoje' },
  { value: 'yesterday', label: 'Ontem' },
  { value: 'thisWeek', label: 'Esta semana' },
  { value: 'last7d', label: 'Últimos 7 dias' },
  { value: 'last30d', label: 'Últimos 30 dias' },
  { value: 'thisMonth', label: 'Este mês' },
  { value: 'lastMonth', label: 'Mês anterior' },
  { value: 'ytd', label: 'Ano atual' },
  { value: 'custom', label: 'Personalizado…' },
];

const selectClassName =
  'font-sans text-sm rounded-sm border pl-3 pr-8 py-2 appearance-none transition-all duration-fast focus:outline-none focus-visible:ring-2';

const selectStyle: React.CSSProperties = {
  background: 'var(--bg-elev-1)',
  borderColor: 'var(--border-strong)',
  color: 'var(--text)',
  boxShadow: 'var(--elev-1), inset 0 1px 2px var(--border-inner-dark)',
  ['--tw-ring-color' as string]: 'rgba(27,58,140,0.15)',
};

function ChevronIcon(): React.JSX.Element {
  return (
    <svg
      className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--text-3)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

/** Hoje no fuso do navegador, em YYYY-MM-DD — teto dos date inputs. */
function todayInputValue(): string {
  const now = new Date();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${mm}-${dd}`;
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

/**
 * Dashboard real com KPIs e gráficos.
 * Substitui o placeholder implementado em F1-S10.
 */
export function DashboardPage(): React.JSX.Element {
  const { user } = useAuth();

  const [range, setRange] = React.useState<Range>('last30d');
  const [cityId, setCityId] = React.useState<string | undefined>(undefined);
  const [customFrom, setCustomFrom] = React.useState<string>('');
  const [customTo, setCustomTo] = React.useState<string>('');

  // Range customizado só vai para a rede quando as duas pontas existem e estão
  // na ordem certa — senão a tela dispararia um 400 a cada tecla no date input.
  const customReady = customFrom !== '' && customTo !== '' && customFrom <= customTo;
  const effectiveRange: Range = range === 'custom' && !customReady ? 'last30d' : range;

  const metricsQuery = {
    range: effectiveRange,
    ...(cityId ? { cityId } : {}),
    ...(effectiveRange === 'custom' ? { dateFrom: customFrom, dateTo: customTo } : {}),
  };

  const { data, isLoading, isFetching, isError, isForbidden, error, updatedAt, refetch } =
    useDashboardMetrics(metricsQuery);

  // ---------------------------------------------------------------------------
  // Opções do seletor de cidade (achado U1)
  //
  // O seletor lia `data.leads.byCity` e só aparecia com length > 1. Como
  // selecionar uma cidade faz a resposta voltar com UMA cidade, o próprio
  // seletor desaparecia depois da primeira escolha — e sumia também durante
  // skeleton e erro. Guardamos aqui a lista completa (a das respostas SEM
  // filtro de cidade), que é estável e continua respeitando o escopo do
  // usuário — diferente de buscar /api/cities, que devolve cidades fora do
  // escopo e levaria o usuário a um 403.
  // ---------------------------------------------------------------------------
  const [cityOptions, setCityOptions] = React.useState<LeadsByCityItem[]>([]);

  React.useEffect(() => {
    if (data !== undefined && cityId === undefined && data.leads.byCity.length > 0) {
      setCityOptions(buildCityOptions(data.leads.byCity));
    }
  }, [data, cityId]);

  const showCitySelect = cityOptions.length > 1;

  // ---------------------------------------------------------------------------
  // Render states
  // ---------------------------------------------------------------------------

  return (
    <div
      className="flex flex-col gap-6"
      style={{
        animation:
          'fade-up var(--dur-slow, 400ms) var(--ease-out, cubic-bezier(0.16,1,0.3,1)) both',
      }}
    >
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-1">
            <h1
              className="font-display font-bold text-ink"
              style={{
                fontSize: 'var(--text-3xl)',
                letterSpacing: '-0.04em',
                fontVariationSettings: "'opsz' 48",
              }}
            >
              Dashboard
            </h1>
            {/* ⓘ tutorial do dashboard — norma 21 §7 */}
            <ContextualHelp featureKey="dashboard.view" className="ml-0.5" />
          </div>
          <p className="font-sans text-sm mt-1" style={{ color: 'var(--text-3)' }}>
            {data?.range.label ?? 'Carregando...'} — bem-vindo, {user?.fullName ?? 'Agente'}.
          </p>
          {/* Frescor explícito (D4): a tela diz de quando são os números. */}
          <div className="mt-2">
            <FreshnessIndicator
              updatedAt={updatedAt}
              isFetching={isFetching}
              onRefresh={() => void refetch()}
              timeZone={data?.range.timeZone}
            />
          </div>
        </div>

        {/* Filtros */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Range select */}
          <div className="relative">
            <label htmlFor="dash-range" className="sr-only">
              Período
            </label>
            <select
              id="dash-range"
              value={range}
              onChange={(e) => setRange(e.target.value as Range)}
              className={selectClassName}
              style={selectStyle}
            >
              {RANGE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <ChevronIcon />
          </div>

          {/* Período personalizado (D5) */}
          {range === 'custom' && (
            <div className="flex items-center gap-2">
              <label htmlFor="dash-from" className="sr-only">
                Data inicial
              </label>
              <input
                id="dash-from"
                type="date"
                value={customFrom}
                max={customTo !== '' ? customTo : todayInputValue()}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="font-sans text-sm rounded-sm border px-3 py-2 transition-all duration-fast focus:outline-none focus-visible:ring-2"
                style={selectStyle}
              />
              <span className="font-sans text-xs" style={{ color: 'var(--text-4)' }}>
                até
              </span>
              <label htmlFor="dash-to" className="sr-only">
                Data final
              </label>
              <input
                id="dash-to"
                type="date"
                value={customTo}
                min={customFrom !== '' ? customFrom : undefined}
                max={todayInputValue()}
                onChange={(e) => setCustomTo(e.target.value)}
                className="font-sans text-sm rounded-sm border px-3 py-2 transition-all duration-fast focus:outline-none focus-visible:ring-2"
                style={selectStyle}
              />
            </div>
          )}

          {/* Cidade select — lista estável, ver comentário do cityOptions */}
          {showCitySelect && (
            <div className="relative">
              <label htmlFor="dash-city" className="sr-only">
                Cidade
              </label>
              <select
                id="dash-city"
                value={cityId ?? ''}
                onChange={(e) => setCityId(e.target.value || undefined)}
                className={selectClassName}
                style={selectStyle}
              >
                <option value="">Todas as cidades</option>
                {cityOptions.map((city) => (
                  <option key={city.cityId} value={city.cityId}>
                    {city.cityName}
                  </option>
                ))}
              </select>
              <ChevronIcon />
            </div>
          )}
        </div>
      </div>

      {/* ── Aviso de período personalizado incompleto ───────────────────────── */}
      {range === 'custom' && !customReady && (
        <p className="font-sans text-xs" style={{ color: 'var(--text-4)' }}>
          Escolha as duas datas para aplicar o período personalizado. Enquanto isso, os números
          abaixo mostram os últimos 30 dias.
        </p>
      )}

      {/* ── Stale Banner ────────────────────────────────────────────────────── */}
      {data && <StaleBanner staleCount={data.leads.staleCount} />}

      {/* ── 403 — sem permissão ─────────────────────────────────────────────── */}
      {isForbidden && (
        <div
          className="rounded-md p-6 text-center"
          style={{
            background: 'var(--danger-bg)',
            borderLeft: '3px solid var(--danger)',
            boxShadow: 'var(--elev-1)',
          }}
        >
          <p className="font-sans font-semibold text-sm" style={{ color: 'var(--danger)' }}>
            Você não tem permissão para visualizar o dashboard.
          </p>
          <p className="font-sans text-sm mt-1" style={{ color: 'var(--text-3)' }}>
            Contate o administrador do sistema.
          </p>
        </div>
      )}

      {/* ── Erro genérico ───────────────────────────────────────────────────── */}
      {isError && !isForbidden && (
        <div
          className="rounded-md p-5 flex items-center justify-between gap-4"
          style={{
            background: 'var(--danger-bg)',
            borderLeft: '3px solid var(--danger)',
            boxShadow: 'var(--elev-1)',
          }}
        >
          <div>
            <p className="font-sans font-semibold text-sm" style={{ color: 'var(--danger)' }}>
              Erro ao carregar o dashboard
            </p>
            <p className="font-sans text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>
              {error?.message ?? 'Erro desconhecido'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => void refetch()}
            className="font-sans text-xs font-semibold px-3 py-1.5 rounded-xs border transition-all duration-fast hover:opacity-80 focus:outline-none focus-visible:ring-2 active:scale-95"
            style={{
              borderColor: 'var(--danger)',
              color: 'var(--danger)',
              ['--tw-ring-color' as string]: 'rgba(200,52,31,0.2)',
            }}
          >
            Tentar novamente
          </button>
        </div>
      )}

      {/* ══ ZONA 1 — NO PERÍODO ═════════════════════════════════════════════ */}
      <ZoneHeading
        title="No período"
        hint="Estes números seguem o filtro de período."
        badge={data?.range.label ?? '—'}
      />

      {isLoading ? (
        <StatsRowSkeleton />
      ) : data ? (
        <StatsRow
          leads={data.leads}
          range={data.range}
          interactionsTotal={data.interactions.totalInRange}
        />
      ) : null}

      {/* Série diária — a visão temporal que não existia (D3) */}
      {isLoading ? (
        <DailySeriesChartSkeleton />
      ) : data ? (
        <DailySeriesChart data={data.series} rangeLabel={data.range.label} />
      ) : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Donut de status */}
        {isLoading ? (
          <StatusDonutSkeleton />
        ) : data ? (
          <StatusDonut data={data.leads.byStatus} />
        ) : null}

        {/* Barras de canal */}
        {isLoading ? (
          <ChannelBarsSkeleton />
        ) : data ? (
          <ChannelBars
            data={data.interactions.byChannel}
            totalInRange={data.interactions.totalInRange}
          />
        ) : null}

        {/* Lista de cidades */}
        {isLoading ? (
          <CityListSkeleton />
        ) : data ? (
          <CityList data={data.leads.byCity} rangeLabel={data.range.label} />
        ) : null}

        {/* Como conheceram o Banco do Povo (origem declarada na qualificação) */}
        {!isLoading && data ? <OriginChannelList data={data.leads.byOriginChannel} /> : null}
      </div>

      {/* ── Top Agentes — desfechos no período ──────────────────────────────── */}
      {isLoading ? (
        <TopAgentsTableSkeleton />
      ) : data ? (
        <TopAgentsTable agents={data.agents.topByLeadsClosed} />
      ) : null}

      {/* ══ ZONA 2 — AGORA ══════════════════════════════════════════════════ */}
      <ZoneHeading
        title="Agora"
        hint="Retrato do estado atual — não muda com o filtro de período."
        badge="snapshot"
        tone="snapshot"
      />

      {isLoading ? (
        <SnapshotRowSkeleton />
      ) : data ? (
        <SnapshotRow leads={data.leads} kanban={data.kanban} />
      ) : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Barras Kanban */}
        {isLoading ? (
          <KanbanBarsSkeleton />
        ) : data ? (
          <KanbanBars data={data.kanban.cardsByStage} />
        ) : null}

        {/*
          Tempo médio por estágio — barras horizontais.

          Antes havia DOIS cards mostrando o mesmo dado em formatos diferentes
          (AvgDaysInStageChart + KanbanAvgDays), lado a lado na mesma tela — o
          achado U4. Ficou o que também mostra o volume de cards por estágio,
          que é o que dá leitura ao tempo médio.
        */}
        {isLoading ? (
          <AvgDaysInStageChartSkeleton />
        ) : data ? (
          <AvgDaysInStageChart
            avgDays={data.kanban.avgDaysInStage}
            cardsByStage={data.kanban.cardsByStage}
          />
        ) : null}
      </div>
    </div>
  );
}
